import { useState } from 'react';
import { collection, orderBy, query } from 'firebase/firestore';
import { callable, db } from '@/lib/firebase';
import { useDocs } from '@/lib/useDocs';
import { messageForAuthError } from '@/lib/authErrors';
import { opportunityStatusLabel } from '@/lib/format';
import type { Opportunity } from '@/types/domain';
import { strings } from '@/strings';

const reviewOpportunity = callable<
  { opportunityId: string; decision: string; note: string },
  { opportunityId: string; status: string }
>('reviewOpportunity');

/** Cards a reviewer still owes a decision on. */
const AWAITING = ['submitted', 'under_review'];

const DECISIONS = [
  { value: 'live', label: strings.oppPublish, primary: true },
  { value: 'draft', label: strings.oppSendBack, primary: false },
  { value: 'paused', label: strings.oppPause, primary: false },
];

/**
 * The review queue renders the tasks and the required competencies inline,
 * because those are the two sections a reviewer actually judges — a list of
 * titles would make "publish" a rubber stamp.
 */
export function OpportunityReviewPage() {
  const { rows, loading, error } = useDocs<Opportunity>(
    query(collection(db, 'opportunities'), orderBy('createdAt', 'desc')),
    []
  );
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const awaiting = rows.filter((row) => AWAITING.includes(row.status));

  async function decide(opportunityId: string, decision: string) {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await reviewOpportunity({ opportunityId, decision, note: notes[opportunityId] || '' });
    } catch (err) {
      setProblem(messageForAuthError((err as Error).message));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <h1 className="text-xl font-bold text-primary">{strings.oppReviewTitle}</h1>
      {loading ? <p className="text-muted">{strings.loading}</p> : null}
      {error ? <p role="alert" className="text-danger">{strings.loadFailed}</p> : null}
      {!loading && !error && awaiting.length === 0 ? <p className="text-muted">{strings.oppReviewEmpty}</p> : null}

      <ul className="flex flex-col gap-4">
        {awaiting.map((card) => (
          <li key={card.id} className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-white p-4">
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="text-lg font-semibold text-ink">{card.title}</span>
              <span className="text-sm text-muted">{card.orgName}</span>
              <span className="text-sm text-muted">{opportunityStatusLabel(card.status)}</span>
            </div>
            <p className="text-ink">{card.summary}</p>

            <ul className="flex flex-col gap-1 text-sm text-muted">
              {(card.tasks || []).map((task, index) => (
                <li key={index}>{task.task} — {task.deliverable}</li>
              ))}
            </ul>

            <p className="text-sm text-muted">
              {strings.oppRequired}: {(card.required || []).map((r) => r.tagId).join('، ')}
            </p>

            <label className="flex flex-col gap-1 text-xs text-muted" htmlFor={`note-${card.id}`}>
              {strings.oppReviewNote}
              <input id={`note-${card.id}`} value={notes[card.id] || ''}
                onChange={(e) => setNotes((prev) => ({ ...prev, [card.id]: e.target.value }))}
                className="rounded-md border border-border-subtle px-2 py-1 text-sm text-ink" />
            </label>

            <div className="flex flex-wrap gap-2">
              {DECISIONS.map((d) => (
                <button key={d.value} type="button" disabled={busy} onClick={() => decide(card.id, d.value)}
                  className={d.primary
                    ? 'min-h-11 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50'
                    : 'min-h-11 rounded-md border border-border-subtle bg-white px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50'}>
                  {d.label}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ul>
      {problem ? <p role="alert" className="text-sm text-danger">{problem}</p> : null}
    </div>
  );
}
